import React, { useEffect, useRef, useState } from 'react';
import {
  REGISTRATION_FIELD_LIMITS,
  isValidRegistrationEmail,
  isValidRegistrationPhone,
  sanitizeRegistrationField,
  sanitizeRegistrationPayload,
} from '../../../utils/inputSecurity';

const validate = ({ name, email, phone }) => {
  const errors = {};

  if (!name.trim()) {
    errors.name = 'Укажите имя';
  }

  if (!email.trim()) {
    errors.email = 'Укажите email';
  } else if (!isValidRegistrationEmail(email.trim())) {
    errors.email = 'Проверьте адрес — похоже, есть опечатка';
  }

  if (!phone.trim()) {
    errors.phone = 'Укажите телефон';
  } else if (!isValidRegistrationPhone(phone.trim())) {
    errors.phone = 'Проверьте номер';
  }

  return errors;
};

/**
 * Форма перед скачиванием. Данные никуда не отправляются и не сохраняются —
 * живут только в состоянии страницы до перезагрузки.
 */
const RegistrationModal = ({ open, onClose, onSubmit, title, description }) => {
  const [values, setValues] = useState({ name: '', email: '', phone: '' });
  const [honeypot, setHoneypot] = useState('');
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false);
  const dialogRef = useRef(null);
  const openedAtRef = useRef(0);

  useEffect(() => {
    if (!open) {
      setTouched(false);
      setErrors({});
      setHoneypot('');
      setValues({ name: '', email: '', phone: '' });
      return undefined;
    }

    openedAtRef.current = Date.now();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    if (dialogRef.current) dialogRef.current.focus();

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const handleChange = (field) => (event) => {
    const next = {
      ...values,
      [field]: sanitizeRegistrationField(field, event.target.value),
    };
    setValues(next);
    if (touched) setErrors(validate(next));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    // Бот: honeypot заполнен или форма отправлена быстрее 800 мс.
    if (honeypot.trim() || Date.now() - openedAtRef.current < 800) {
      return;
    }

    const safe = sanitizeRegistrationPayload(values);
    const nextErrors = validate(safe);
    setErrors(nextErrors);
    setTouched(true);

    if (!Object.keys(nextErrors).length) {
      onSubmit(safe);
    }
  };

  return (
    <div className="dg-modal-overlay" role="presentation" onMouseDown={onClose}>
      <div
        className="dg-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dg-modal-title"
        tabIndex={-1}
        ref={dialogRef}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button type="button" className="dg-modal-close" onClick={onClose} aria-label="Закрыть">
          ×
        </button>

        <h3 className="dg-modal-title" id="dg-modal-title">
          {title}
        </h3>
        <p className="dg-modal-text">{description}</p>

        <form className="dg-modal-form" onSubmit={handleSubmit} noValidate autoComplete="on">
          {/* Honeypot: скрытое поле для ботов */}
          <div className="dg-honeypot" aria-hidden="true">
            <label htmlFor="dg-reg-website">Website</label>
            <input
              id="dg-reg-website"
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(event) => setHoneypot(event.target.value)}
            />
          </div>

          <div className="dg-field">
            <label className="dg-field-label" htmlFor="dg-reg-name">
              Как вас зовут
            </label>
            <input
              id="dg-reg-name"
              className="dg-field-input"
              type="text"
              name="name"
              value={values.name}
              onChange={handleChange('name')}
              autoComplete="name"
              maxLength={REGISTRATION_FIELD_LIMITS.name}
              spellCheck="false"
            />
            {errors.name ? <p className="dg-field-error">{errors.name}</p> : null}
          </div>

          <div className="dg-field">
            <label className="dg-field-label" htmlFor="dg-reg-email">
              Email
            </label>
            <input
              id="dg-reg-email"
              className="dg-field-input"
              type="email"
              name="email"
              value={values.email}
              onChange={handleChange('email')}
              autoComplete="email"
              maxLength={REGISTRATION_FIELD_LIMITS.email}
              inputMode="email"
              spellCheck="false"
            />
            {errors.email ? <p className="dg-field-error">{errors.email}</p> : null}
          </div>

          <div className="dg-field">
            <label className="dg-field-label" htmlFor="dg-reg-phone">
              Телефон
            </label>
            <input
              id="dg-reg-phone"
              className="dg-field-input"
              type="tel"
              name="phone"
              value={values.phone}
              onChange={handleChange('phone')}
              placeholder="+7 700 000 00 00"
              autoComplete="tel"
              maxLength={REGISTRATION_FIELD_LIMITS.phone}
              inputMode="tel"
            />
            {errors.phone ? <p className="dg-field-error">{errors.phone}</p> : null}
          </div>

          <button type="submit" className="dg-btn dg-btn--primary dg-btn--full">
            Продолжить
          </button>

          <p className="dg-modal-note">
            Данные нужны только чтобы открыть скачивание. Мы их никуда не отправляем и не храним.
          </p>
        </form>
      </div>
    </div>
  );
};

export default RegistrationModal;
